import React, { useState, useCallback } from 'react';
import { Chain, ChainClient, SetChainProductLineRequest } from '../../../services/WebApiClient';
import { replaceInArray } from '../../../util/replaceInArray';
import { useClient } from '../../../util/useClient';
import { usePromise } from '../../EditorCommon/usePromise';
import { LoadSpinner } from '../../LoadSpinner';
import { IColumn, Table } from '../../Table/Table';

interface ICpl {
    productLineId: number;
    isActive: boolean;
    originalName: string;
    customName: string;
    isChanged: boolean;
}

export function ChainProductLineAdmin(props: {
    chain: Chain;
    onChange: () => void
}) {

    const client = useClient(ChainClient);
    const [chainProductLines, setChainProductLines] = useState<ICpl[] | undefined>();
    const [updateCount, setUpdateCount] = useState(0);

    usePromise(useCallback(async () => {
        const uc = updateCount;
        const response = await client.getChainProductLines(props.chain.id);
        setChainProductLines(response.chainProductLines.map(cpl => ({
            productLineId: cpl.productLineId,
            isActive: cpl.isActive,
            customName: cpl.customName ?? '',
            originalName: cpl.defaultName,
            isChanged: false
        })));
        return {
            chainProductLines: response.chainProductLines,
            uc,
        };
    },
        [client, props.chain, updateCount]
    ));

    function replaceCpl(productLineId: number, replacer: (original: ICpl) => ICpl) {
        if (!chainProductLines)
            return;
        const newState = replaceInArray(chainProductLines,
            c => c.productLineId === productLineId,
            c => ({ ...replacer(c), isChanged: true })
        ) ?? chainProductLines;
        setChainProductLines(newState);
    }
    function setIsActive(cpl: ICpl, isActive: boolean) {
        replaceCpl(cpl.productLineId,
            c => ({ ...c, isActive })
        );
    }

    function setName(cpl: ICpl, name: string) {
        replaceCpl(cpl.productLineId,
            c => ({ ...c, customName: name })
        );
    }

    async function save() {
        if (!chainProductLines)
            return;
        setChainProductLines(undefined);
        try {
            const requests = chainProductLines.filter(c => c.isChanged)
                .map(c => client.setChainProductLine(
                    props.chain.id,
                    c.productLineId,
                    new SetChainProductLineRequest({
                        isActive: c.isActive,
                        name: c.customName === ''
                            ? null
                            : c.customName
                    })
                ));
            await Promise.allSettled(requests);

        }
        finally {
            setUpdateCount(updateCount + 1);
            props.onChange();
        }
    }


    const columns: IColumn<ICpl>[] = [
        {
            content: cpl => cpl.originalName,
            key: "originalName",
            header: "Original Name"
        },
        {
            content: cpl => (<input type="checkbox"
                checked={cpl.isActive}
                className="form-check-input"
                onChange={(evt) => setIsActive(cpl, evt.target.checked)} />),
            key: "isActive",
            header: "Active",
        },
        {
            content: (cpl) => (<input type="text" value={cpl.customName} onChange={(evt) => setName(cpl, evt.target.value)} />),
            key: "customName",
            header: "Custom Name",
        }
    ];

    if (!chainProductLines)
        return <LoadSpinner />;

    return (
        <>
            <Table columns={columns} rows={chainProductLines} rowKey={cpl => cpl.productLineId} />
            <button className="btn btn-primary"
                onClick={save}>
                Save
            </button>
        </>
    );
}
